import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
} from "@mui/material";
import { getListUser } from "entities/report_revenue/service";
import React, { useEffect, useRef, useState } from "react";

// Same server-search + infinite-scroll-listbox pattern as
// entities/course/components/select.teacher.tsx, generalized to list every
// user (not just teachers) and debounced, since select.teacher.tsx refires
// on every keystroke.
const SelectAuthor = ({
  callback,
  selectItem,
  customWidth,
}: {
  callback: (item: any) => void;
  selectItem: string;
  customWidth?: string;
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const listboxRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const delay = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(delay);
  }, [searchTerm]);

  const fetchUsers = async (search = "", newPage = 1, reset = false) => {
    if (!hasMore && !reset) return;

    setLoading(true);
    try {
      const response = await getListUser({
        limit: 10,
        page: newPage,
        search,
      } as any);

      if (reset) {
        setUsers(response.data || []);
      } else {
        setUsers((prev) => [...prev, ...response.data]);
      }

      if (response.data.length < 10) {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Lỗi khi lấy danh sách người dùng:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    setPage(1);
    setHasMore(true);
    fetchUsers(debouncedSearchTerm, 1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (page > 1) {
      fetchUsers(debouncedSearchTerm, page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleScroll = (event: React.SyntheticEvent) => {
    const listboxNode = listboxRef.current;
    if (!listboxNode) return;

    const { scrollTop, scrollHeight, clientHeight } = listboxNode;
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  return (
    <Box flex={1} width={customWidth || "100%"}>
      <Autocomplete
        size="small"
        value={selectItem ? users.find((u) => u._id === selectItem) ?? null : null}
        onChange={(event, newValue) => {
          if (!newValue) {
            callback("");
          } else {
            callback(newValue._id);
          }
        }}
        inputValue={searchTerm}
        onInputChange={(event, newInputValue) => {
          setSearchTerm(newInputValue);
        }}
        options={users}
        getOptionLabel={(option) => option.display_name || ""}
        isOptionEqualToValue={(option, value) => option._id === value._id}
        loading={loading}
        ListboxProps={{
          ref: listboxRef,
          onScroll: handleScroll,
        }}
        onOpen={() => {
          if (users.length === 0) {
            fetchUsers("", 1, true);
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Tác giả"
            placeholder="Tìm tác giả"
            variant="outlined"
            size="small"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress color="inherit" size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
};

export default SelectAuthor;
